// WAAP "API Protection → Threat Detection" — Overview (native BI charts, board 61)
// + Threats (paginated list) + Detection Rules (custom rules CRUD).
// Account-level (any of the account's domain ids resolves the customer).
//   GET        /api-protection/threat-detection/charts?tz=&range=
//   GET        /api-protection/threat-detection/threats?pageNumber=&length=
//   GET/POST   /api-protection/threat-detection/rules
//   GET/PUT/DELETE /api-protection/threat-detection/rules/:ruleId
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';
import type { WaapChartsResponse } from './dashboard-charts';
import type { ApiProtectionTimeRange } from './api-protection-overview-charts';

// ---- Detection rule DTOs (mirror eagle_waf/apiprotection/types.go) ----------

export interface WaapThreatRiskLevel {
  /** low | medium | high */
  value: string;
  label: string;
}

/** One triggering condition (Metric / Matching condition / Value). */
export interface WaapThreatCondition {
  key: string; // status | count_request | ...
  label: string;
  operation: string; // == | > | < | >= | <= | !=
  sql_operation: string; // portal mirror of operation
  value: number | string;
}

export interface WaapThreatSource {
  type: string; // ip | ...
  action?: string; // region | ...
  value: unknown; // "all" or a list
}

export interface WaapThreatTarget {
  type: string; // all | app | api | tag
  value: string[]; // [] for "all"; app/api/tag carry a list of ids e.g. ["247","57"]
}

export interface WaapThreatAccessTimeValue {
  weeks: string[]; // "1".."7"
  start_time: string;
  end_time: string;
}

export interface WaapThreatAccessTime {
  type: string; // all (Always) | fixed (Periodic)
  value: WaapThreatAccessTimeValue;
}

export interface WaapThreatRuleConfig {
  enable: boolean;
  rule_type: string; // custom
  description: string;
  risk_level: WaapThreatRiskLevel;
  source: WaapThreatSource;
  target: WaapThreatTarget;
  stat_period: string; // once | minute | ...
  access_time: WaapThreatAccessTime;
  rules: WaapThreatCondition[];
  /** Present only when access_time.type = "fixed" (Periodic): [startISO, endISO]
   *  in UTC for today, mirroring the start_time/end_time window. */
  time_range?: [string, string];
}

/** One threat detection rule. Type 1 = system preset, 2 = custom. */
export interface WaapThreatRule {
  id: number;
  name: string;
  type: number;
  config: WaapThreatRuleConfig;
  created?: number;
  updated?: number;
  status?: number;
  customer_id?: number;
}

export interface WaapThreatRules {
  rules: WaapThreatRule[];
}

/** One page of detected API threats. The item shape is passed through raw (the
 *  live account returned an empty list, so columns are read best-effort). */
export interface WaapThreatsPage {
  totalCount: number;
  pageSize: number;
  startIndex: number;
  endIndex: number;
  currentPageNumber: number;
  totalPageNumber: number;
  hasPre: boolean;
  hasNext: boolean;
  list: Record<string, unknown>[];
}

// ---- Overview (native BI charts) --------------------------------------------

/** Read the normalized Threat Detection overview widgets (Superset board 61) for
 *  a website's account. `range` drives the dashboard's Time Range template filter.
 *  Returns null when the board is not configured server-side (400/404/501). */
export function useWaapThreatDetectionCharts(
  domainId: string | undefined,
  range: ApiProtectionTimeRange,
) {
  return useQuery({
    queryKey: [...waapKeys.aspect(domainId ?? '', WAAP_AREAS.apiProtectionThreatCharts), range],
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const path = waapPath(domainId as string, WAAP_AREAS.apiProtectionThreatCharts);
        const res = await serviceApiV3.get<WaapChartsResponse>(
          `${path}?tz=${encodeURIComponent(tz)}&range=${encodeURIComponent(range)}`,
        );
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}

// ---- Threats (paginated list) -----------------------------------------------

const threatsBase = (domainId: string) => waapPath(domainId, WAAP_AREAS.apiProtectionThreats);
const threatsKey = (domainId: string) => waapKeys.aspect(domainId, WAAP_AREAS.apiProtectionThreats);

export interface WaapThreatsQuery {
  page?: number;
  length?: number;
}

/** List detected API threats (paginated). Returns null on 400/404 (not ready). */
export function useWaapThreats(domainId: string | undefined, q: WaapThreatsQuery = {}) {
  const { page = 1, length = 25 } = q;
  return useQuery({
    queryKey: [...threatsKey(domainId ?? ''), page, length],
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapThreatsPage | null> => {
      try {
        const params = new URLSearchParams({ pageNumber: String(page), length: String(length) });
        const res = await serviceApiV3.get<WaapThreatsPage>(
          `${threatsBase(domainId as string)}?${params.toString()}`,
        );
        return res.data ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

// ---- Detection Rules (CRUD) -------------------------------------------------

const rulesBase = (domainId: string) => waapPath(domainId, WAAP_AREAS.apiProtectionThreatRules);
const rulesKey = (domainId: string) => waapKeys.aspect(domainId, WAAP_AREAS.apiProtectionThreatRules);

/** List all threat detection rules (system + custom). */
export function useWaapThreatRules(domainId: string | undefined) {
  return useQuery({
    queryKey: rulesKey(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapThreatRule[]> => {
      const res = await serviceApiV3.get<WaapThreatRules>(rulesBase(domainId as string));
      return res.data?.rules ?? [];
    },
  });
}

/** Create a custom threat detection rule. */
export function useCreateWaapThreatRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapThreatRule>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WaapThreatRule) => (await serviceApiV3.post(rulesBase(domainId), body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: rulesKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Edit a threat detection rule. */
export function useUpdateWaapThreatRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { id: number; body: WaapThreatRule }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapThreatRule }) =>
      (await serviceApiV3.put(`${rulesBase(domainId)}/${id}`, body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: rulesKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a threat detection rule. */
export function useDeleteWaapThreatRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await serviceApiV3.delete(`${rulesBase(domainId)}/${id}`)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: rulesKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}
